import {
  Timestamp,
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import { InboxMessageSchema, type InboxMessage } from '@kilat/shared';

import { db } from '../firebase.js';

/**
 * users/{uid}/inbox/{msgId} — the agent's (and recruiter's) in-app inbox.
 *
 * The server already wrote milestone messages here (gap G26 fan-out), and from
 * the transactional pass also sign-ups, commissions and payouts; until this
 * reader existed no agent screen showed them.
 *
 * Bounded (`orderBy(createdAt desc).limit(N)`) so the bell + list cost
 * O(bounded) reads. A malformed doc is skipped, not fatal. The only client
 * write the rules allow is stamping `readAt` on one's own message.
 */
export const INBOX_PAGE_SIZE = 30;

export function subscribeAgentInbox(
  uid: string,
  cb: (messages: InboxMessage[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, `users/${uid}/inbox`),
    orderBy('createdAt', 'desc'),
    limit(INBOX_PAGE_SIZE),
  );
  return onSnapshot(
    q,
    (snap) => {
      const out: InboxMessage[] = [];
      for (const d of snap.docs) {
        const parsed = InboxMessageSchema.safeParse(d.data());
        if (parsed.success) out.push({ ...parsed.data, id: d.id });
      }
      cb(out);
    },
    (err) => onError(err),
  );
}

/** Agent- and recruiter-facing messages (and older ones written before `audience`). */
export function isAgentInboxMessage(m: InboxMessage): boolean {
  return m.audience === undefined || m.audience === 'agent' || m.audience === 'recruiter';
}

/** Stamp `readAt` on several messages in one batched write. */
export async function markInboxRead(uid: string, ids: readonly string[]): Promise<void> {
  if (ids.length === 0) return;
  const batch = writeBatch(db);
  const now = Timestamp.now();
  for (const id of ids) batch.update(doc(db, `users/${uid}/inbox`, id), { readAt: now });
  await batch.commit();
}
